import React, { useEffect, useState, useCallback } from "react";
import Shell from "@/components/app/Shell";
import RequestCard from "@/components/app/RequestCard";
import NewRequestDialog from "@/components/app/NewRequestDialog";
import api, { formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { STATUS_META } from "@/lib/status";
import { Plus, ShieldCheck, Package, BarChart3, ClipboardList, Trash2, Download, CheckCircle2, XCircle, Clock, Wrench, Timer } from "lucide-react";
import { toast } from "sonner";

export default function ManufacturerDashboard() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/requests");
      setRequests(data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const warrantyReqs = requests.filter((r) => r.is_warranty || r.service_type === "Garantia");

  return (
    <Shell
      title={`Área do fabricante · ${user?.brand || ""}`}
      subtitle="Acompanhe chamados, aprove garantias, controle peças e veja indicadores da sua marca"
      actions={
        <Button className="bg-blue-600 hover:bg-blue-700 gap-1.5" onClick={() => setNewOpen(true)} data-testid="fab-new-request-button">
          <Plus className="h-4 w-4" /> Abrir chamado
        </Button>
      }
    >
      <Tabs defaultValue="indicadores">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="indicadores" data-testid="fab-tab-indicadores"><BarChart3 className="h-4 w-4 mr-1" /> Indicadores</TabsTrigger>
          <TabsTrigger value="chamados" data-testid="fab-tab-chamados"><ClipboardList className="h-4 w-4 mr-1" /> Chamados</TabsTrigger>
          <TabsTrigger value="garantias" data-testid="fab-tab-garantias"><ShieldCheck className="h-4 w-4 mr-1" /> Garantias</TabsTrigger>
          <TabsTrigger value="pecas" data-testid="fab-tab-pecas"><Package className="h-4 w-4 mr-1" /> Peças</TabsTrigger>
        </TabsList>

        <TabsContent value="indicadores"><IndicatorsTab /></TabsContent>

        <TabsContent value="chamados">
          {loading ? <p className="text-slate-400 text-sm">Carregando...</p>
            : requests.length === 0 ? <Empty icon={ClipboardList} text="Nenhum chamado da sua marca ainda." />
            : <div className="grid gap-4 sm:grid-cols-2" data-testid="fab-requests-list">
                {requests.map((r) => <RequestCard key={r.id} request={r} showCustomer />)}
              </div>}
        </TabsContent>

        <TabsContent value="garantias">
          {warrantyReqs.length === 0 ? <Empty icon={ShieldCheck} text="Nenhum chamado de garantia da sua marca." />
            : <div className="grid gap-4 sm:grid-cols-2" data-testid="fab-warranty-list">
                {warrantyReqs.map((r) => (
                  <RequestCard key={r.id} request={r} showCustomer
                    actions={<WarrantyActions request={r} onDone={load} />} />
                ))}
              </div>}
        </TabsContent>

        <TabsContent value="pecas"><PartsTab /></TabsContent>
      </Tabs>

      <NewRequestDialog open={newOpen} onOpenChange={setNewOpen} onCreated={load} managerMode />
    </Shell>
  );
}

function Empty({ icon: Icon, text }) {
  return (
    <div className="text-center py-16" data-testid="fab-empty">
      <Icon className="h-12 w-12 text-slate-300 mx-auto mb-3" />
      <p className="text-slate-500">{text}</p>
    </div>
  );
}

function IndicatorsTab() {
  const [m, setM] = useState(null);
  const [downloading, setDownloading] = useState(false);
  useEffect(() => { api.get("/manufacturer/metrics").then(({ data }) => setM(data)).catch(() => setM(null)); }, []);

  const exportCsv = async () => {
    setDownloading(true);
    try {
      const { data } = await api.get("/manufacturer/report.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `relatorio_${m?.brand || "marca"}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Falha ao exportar relatório."); }
    finally { setDownloading(false); }
  };

  if (!m) return <p className="text-slate-400 text-sm">Carregando...</p>;
  return (
    <div className="space-y-4" data-testid="fab-indicators">
      <div className="flex justify-end">
        <Button variant="outline" className="gap-1.5" onClick={exportCsv} disabled={downloading} data-testid="fab-export-csv">
          <Download className="h-4 w-4" /> {downloading ? "Gerando..." : "Exportar CSV"}
        </Button>
      </div>
      <div className="grid gap-4 sm:grid-cols-4">
        <Stat icon={ClipboardList} color="blue" label="Total de chamados" value={m.total} testid="fab-stat-total" />
        <Stat icon={CheckCircle2} color="emerald" label="Garantias aprovadas" value={m.warranty.aprovado} testid="fab-stat-aprovadas" />
        <Stat icon={Clock} color="amber" label="Garantias pendentes" value={m.warranty.pendente} testid="fab-stat-pendentes" />
        <Stat icon={Timer} color="blue" label="Tempo médio (h)" value={m.avg_service_hours} testid="fab-stat-tempo" />
      </div>
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold text-slate-500 mb-3">Chamados por status</h3>
          <div className="grid gap-2 sm:grid-cols-3">
            {Object.entries(m.by_status).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between rounded-md border border-slate-100 bg-slate-50 px-3 py-2" data-testid={`fab-status-${k}`}>
                <span className="text-sm text-slate-600">{(STATUS_META[k] || {}).label || k}</span>
                <span className="font-semibold text-slate-800">{v}</span>
              </div>
            ))}
          </div>
          <p className="text-sm text-slate-500 mt-4 flex items-center gap-2"><Wrench className="h-4 w-4 text-[#1e7be0]" /> Peças utilizadas (total): <b className="text-slate-800">{m.parts_total}</b></p>
        </CardContent>
      </Card>
    </div>
  );
}

const COLORS = {
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
  amber: "bg-amber-50 text-amber-700 border-amber-100",
  blue: "bg-blue-50 text-blue-700 border-blue-100",
};
function Stat({ icon: Icon, color, label, value, testid }) {
  return (
    <Card className="border-slate-200" data-testid={testid}>
      <CardContent className="p-4 flex items-start justify-between">
        <div>
          <p className="text-xs text-slate-500">{label}</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{value}</p>
        </div>
        <div className={`h-10 w-10 rounded-lg border flex items-center justify-center ${COLORS[color]}`}><Icon className="h-5 w-5" /></div>
      </CardContent>
    </Card>
  );
}

function WarrantyActions({ request, onDone }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("aprovado");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const st = request.warranty_approval_status;

  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/requests/${request.id}/warranty-approval`, { status, note: note || null });
      toast.success(`Garantia ${status}!`);
      setOpen(false); onDone?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="flex gap-2 flex-1">
        <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700 gap-1" onClick={() => { setStatus("aprovado"); setOpen(true); }} data-testid={`warranty-approve-${request.id}`}>
          <CheckCircle2 className="h-4 w-4" /> Aprovar
        </Button>
        <Button size="sm" variant="outline" className="flex-1 border-red-300 text-red-600 hover:bg-red-50 gap-1" onClick={() => { setStatus("reprovado"); setOpen(true); }} data-testid={`warranty-reject-${request.id}`}>
          <XCircle className="h-4 w-4" /> Reprovar
        </Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="warranty-dialog">
          <DialogHeader><DialogTitle>{status === "aprovado" ? "Aprovar garantia" : "Reprovar garantia"}</DialogTitle></DialogHeader>
          <div className="space-y-1.5">
            <Label>Observação (opcional)</Label>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Motivo / observações" data-testid="warranty-note-input" />
          </div>
          <DialogFooter>
            <Button className={`w-full ${status === "aprovado" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}`} onClick={submit} disabled={busy} data-testid="warranty-confirm-button">
              {busy ? "Enviando..." : (status === "aprovado" ? "Confirmar aprovação" : "Confirmar reprovação")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PartsTab() {
  const [parts, setParts] = useState([]);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { api.get("/manufacturer/parts").then(({ data }) => setParts(data)); }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/manufacturer/parts", { name, code: code || null });
      setName(""); setCode(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  const remove = async (id) => { await api.delete(`/manufacturer/parts/${id}`); load(); };

  return (
    <div className="space-y-4" data-testid="fab-parts">
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <form onSubmit={add} className="flex flex-wrap gap-2 items-end">
            <div className="space-y-1.5 flex-1 min-w-[180px]"><Label>Nome da peça</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Placa eletrônica" data-testid="part-name-input" /></div>
            <div className="space-y-1.5 w-40"><Label>Código</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Opcional" data-testid="part-code-input" /></div>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700 gap-1.5" disabled={busy} data-testid="part-add-button">
              <Plus className="h-4 w-4" /> Adicionar
            </Button>
          </form>
        </CardContent>
      </Card>
      {parts.length === 0 ? <Empty icon={Package} text="Nenhuma peça cadastrada." />
        : <div className="grid gap-2 sm:grid-cols-2" data-testid="fab-parts-list">
            {parts.map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2.5" data-testid={`part-${p.id}`}>
                <div className="flex items-center gap-2">
                  <Wrench className="h-4 w-4 text-[#1e7be0]" />
                  <span className="text-sm text-slate-700">{p.name}{p.code ? ` · ${p.code}` : ""}</span>
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => remove(p.id)} data-testid={`part-remove-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>}
    </div>
  );
}
